import { SnackBarService } from './../../core/services/snack-bar.service';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserModel } from '../../core/data-sources/user-data-source.service';
import { UserRoles } from '../../../models/User';
import { ErrorsEnum, ErrorService } from '../../core/services/error.service';
import { ActionService } from '../../core/services/action.service';
import { FirebaseService } from 'src/app/core/services/firebase.service';

@Component({
  selector: 'app-add-or-edit-user-modal',
  templateUrl: './add-or-edit-user-modal.component.html',
  styleUrls: ['./add-or-edit-user-modal.component.scss']
})
export class AddOrEditUserModalComponent implements OnInit {

  form: FormGroup;
  roleOptions: { value: UserRoles, label: string }[];
  modalType: 'add' | 'edit';
  btnLabel: string;
  title: string;
  isLoading: boolean;
  imageName = '';
  image;
  hidePassword = true;
  passwordPatternMessage = 'A senha deve conter de 6 a 8 números.';
  @ViewChild('notification', { static: true }) notificationTmpl;
  notificationData: { type: string, title: string, message: string } | null;

  constructor(public dialogRef: MatDialogRef<AddOrEditUserModalComponent>,
              @Inject(MAT_DIALOG_DATA) public data: { definedRole: UserRoles, user: UserModel, disabledRole: UserRoles },
              private formBuilder: FormBuilder,
              private actionService: ActionService,
              private errorService: ErrorService,
              private firebaseService: FirebaseService,
              private snackBarService: SnackBarService) {
    this.isLoading = false;
    this.roleOptions = [
      { value: UserRoles.ADMIN, label: 'Administrador' },
      { value: UserRoles.MANAGER, label: 'Gestor' },
      { value: UserRoles.OPERATOR, label: 'Operador' }
    ];
  }

  ngOnInit(): void {
    this.buildForm();
    this.modalType = 'add';
    this.btnLabel = 'Criar novo usuário';
    this.title = 'Cadastrar novo usuário';
    if (this.data.user) {
      this.modalType = 'edit';
      this.btnLabel = 'Editar usuário';
      this.title = 'Editar usuário';
      this.form.controls.username.disable();
      this.form.controls.password.disable();
      if (this.data.user.type === UserRoles.MASTER) {
        this.form.disable();
      }
      this.setFormValues();
    }

    if (this.data?.definedRole) {
      this.form.controls.type.setValue(this.data.definedRole);
      this.form.controls.type.disable();
    }

    if (this.data.definedRole === 'ADMIN') {
      this.passwordPatternMessage = 'A senha deve conter de 6 e 20 caracteres alfanuméricos. Não use caracteres especiais.';
    }
  }

  buildForm() {
    const pattern = this.data.definedRole === 'ADMIN' ? /^[A-Za-z0-9]{6,20}$/ : /^\d{6,8}$/;
    this.form = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      username: ['', Validators.required],
      email: ['', Validators.required],
      type: [null, Validators.required],
      password: ['', [Validators.required, Validators.pattern(pattern)]]
    });
  }

  setFormValues(): void {
    const formControls = this.form.controls;
    formControls.firstName.setValue(this.data.user.firstName);
    formControls.lastName.setValue(this.data.user.lastName);
    formControls.username.setValue(this.data.user.username);
    formControls.email.setValue(this.data.user.email);
    formControls.type.setValue(this.data.user.type);
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      try {
        this.isLoading = true;
        const formControls = this.form.controls;
        const user: any = {
          firstName: formControls.firstName.value,
          lastName: formControls.lastName.value,
          username: formControls.username.value,
          email: formControls.email.value,
          type: formControls.type.value,
          password: formControls.password.value
        };
        if (this.modalType === 'add') {
          await this.actionService.createUser(user);
          this.dialogRef.close(user.username);
          if (this.image) {
            await this.uploadPicture();
          }
        } else {
          this.removeNullKeys(user);
          user.id = this.data.user.id;
          delete user.username;
          await this.actionService.editUser(user);
          if (this.image) {
            await this.uploadPicture();
          }
          this.dialogRef.close(user.firstName);
        }
      } catch (e) {
        this.setSnackBarErrors(e);
        this.setFormErrors(e.error);
      } finally {
        this.isLoading = false;
      }
    } else if (this.image) {
      await this.uploadPicture();
    }
  }

  removeNullKeys(params) {
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === '') {
        delete params[key];
      }
    });
  }

  setSnackBarErrors(e): void {
    switch (e.error.type) {
      case ErrorsEnum.USER_ERROR_EDIT_SELF_TYPE:
        this.errorService.openErrorSnackBarWithCustomMessage('O usuário não pode editar seu próprio cargo.');
        break;
      case ErrorsEnum.USER_ERROR_INVALID_PERMISSION:
        this.errorService.openErrorSnackBarWithCustomMessage('O usuário não possui permissões necessárias para criar ou editar este usuário.');
        break;
      default:
        this.errorService.openErrorSnackBar(e);
        break;
    }
  }

  setFormErrors(error) {
    switch (error.type) {
      case ErrorsEnum.USER_ERROR_USER_ALREADY_EXISTS:
        if (error.data.username) {
          this.form.controls.username.setErrors({alreadyExist: true});
        }
        if (error.data.email) {
          this.form.controls.email.setErrors({alreadyExist: true});
        }
        break;
      case ErrorsEnum.VALIDATION_ERROR:
        if (error?.data?.filter(e => e.type === 'email')?.length) {
          this.form.controls.email.setErrors({invalid: true});
        }
        break;
    }
  }

  close(): void {
    this.form.reset();
    this.clearImageFile();
    this.dialogRef.close();
  }

  onImageSelected(event) {
    this.image = event.target.files[0];
    this.imageName = this.image.name;
  }

  clearImageFile() {
    this.image = null;
    this.imageName = null;
  }

  async uploadPicture(): Promise<void> {
    await this.firebaseService.uploadUserPicture(this.data.user.id, this.image).then(() => {
      this.firebaseService.imageUploaded$.next(true);
      this.notificationData = {
        type: 'success',
        title: 'Imagem atualizada com sucesso!',
        message: `A imagem de perfil do usuário foi atualizada com sucesso.`
      };
      const notificationId = '95';
      this.snackBarService.showFeedback(this.notificationTmpl, this.notificationData, notificationId);
      setTimeout(() => {
        this.notificationData = null;
        this.snackBarService.hideNotification(notificationId);
      }, 5000);
      this.close();
    }).catch((err) => {
      console.log(err);
      this.notificationData = {
        type: 'alert',
        title: 'Erro no upload',
        message: "Erro ao atualizar a imagem de perfil do usuário. Por favor, tente novamente."
      };
      const notificationId = '94';
      this.snackBarService.showFeedback(this.notificationTmpl, this.notificationData, notificationId);
      setTimeout(() => {
        this.notificationData = null;
        this.snackBarService.hideNotification(notificationId);
      }, 5000);
      this.close();
    });
  }
}
